import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import {useCallback} from 'react'

import {useAppSelector} from '../../../../../../../hooks/react-redux'
import {getBuild} from '../../../../../../../selectors'
import BuildLogBuildNavigationWrapper from '../../../../../../packages/BuildLog/FullBuildLog/BuildLogBuildNavigationWrapper/BuildLogBuildNavigationWrapper'
import {SELECTED_BUILD_DEPENDENCY_ID_PARAM} from '../../../../../../packages/BuildLog/FullBuildLog/BuildLogBuildNavigationWrapper/BuildLogBuildNavigationWrapper.consts'
import {
  AgentBadge,
  JobTimeBadge as AdvancedTimeBadge,
  StatusBadge,
} from '../../../../components/Badges'
import TimeBadge from '../../../../components/Badges/TimeBadge/TimeBadge/TimeBadge'
import {isNewTimeBadgeEnabled} from '../../../../utils/featureToggles'

import {useJobDetailsHeader, useParallelizedJobDetailsHeader} from './JobDetailsHeader.hooks'

import styles from './JobDetailsHeader.css'

import IconButton from 'src/components/common/IconButton/IconButton'
import {useBooleanQueryParamState, useQueryParam} from 'src/hooks/routes'
import {getHrefWithQueryParams} from 'src/routes'
import type {BuildId} from 'src/types'
import {toBuildId} from 'src/types'

type Props = {
  className?: string
  jobId: BuildId
}

function JobDetailsHeader({className, jobId}: Props) {
  const parallelized = useAppSelector(state => getBuild(state, jobId)?.parallelized)
  const pageFullLogBuildIdParam = useQueryParam(SELECTED_BUILD_DEPENDENCY_ID_PARAM)
  const selectedNavigationId =
    pageFullLogBuildIdParam != null ? toBuildId(pageFullLogBuildIdParam) : null

  const [isJobDetailsFullscreen, setJobDetailsFullscreen] = useBooleanQueryParamState(
    'jobDetailsFullscreen',
    false,
  )

  const {navigate, location, jobName, jobStatus, isJobRunning, isJobReused} =
    useJobDetailsHeader(jobId)
  const {batchJobStatus, isBatchJobRunning, isBatchJobReused} =
    useParallelizedJobDetailsHeader(selectedNavigationId)

  const toggleFullscreen = useCallback(() => {
    setJobDetailsFullscreen(!isJobDetailsFullscreen, true)
  }, [isJobDetailsFullscreen, setJobDetailsFullscreen])

  const closeSidebar = useCallback(() => {
    const href = getHrefWithQueryParams(location, null, () => ({}))
    navigate(href, {replace: true})
  }, [location, navigate])

  return (
    <div className={classNames(className, styles.header)}>
      <div className={styles.heading}>
        <H3>{jobName}</H3>
        {isJobReused && <span className={styles.reusedLabel}>{'Reused job'}</span>}
      </div>
      <div className={styles.actions}>
        {!isJobDetailsFullscreen && (
          <IconButton
            className={styles.actionButton}
            title="Enter full screen"
            icon="fullscreen"
            onClick={toggleFullscreen}
          />
        )}
        <IconButton
          className={styles.actionButton}
          title="Close job sidebar"
          icon="close"
          onClick={isJobDetailsFullscreen ? toggleFullscreen : closeSidebar}
        />
      </div>
      <div className={styles.subheading}>
        {parallelized && (
          <BuildLogBuildNavigationWrapper className={styles.navigation} buildId={jobId} />
        )}
        <div className={styles.badges}>
          <StatusBadge
            status={batchJobStatus != null ? batchJobStatus : jobStatus}
            isRunning={isBatchJobRunning != null ? isBatchJobRunning : isJobRunning}
          />
          {isNewTimeBadgeEnabled ? (
            <AdvancedTimeBadge runId={selectedNavigationId ?? jobId} />
          ) : (
            <TimeBadge
              runId={selectedNavigationId ?? jobId}
              durationLabel={
                (isBatchJobReused != null ? isBatchJobReused : isJobReused)
                  ? 'Last duration'
                  : undefined
              }
            />
          )}
          <AgentBadge runId={selectedNavigationId ?? jobId} />
        </div>
      </div>
    </div>
  )
}

export default JobDetailsHeader
